import { useExplore } from "@modules/new-explore/hooks/useExplore";
import { EXPLORE_SLUG } from "types/api/explore";
import {
  NewExploreInstructor,
  NewExploreLanding,
  NewExploreProduct,
} from "types/api/new-explore";
import { ICard, ILandingCard } from "@shared/components/card/types";
import { useRouter } from "next/router";
import { CARD_TAG } from "@shared/components/card/types";
import useExplorePreloadedData from "./useExplorePreloadedData";
import { COURSE_TYPE } from "@types";

export const useExplorePage = () => {
  const { query } = useRouter();
  const { addFilter } = useExplore();

  const slug =
    typeof query.slug === "string"
      ? (query.slug as EXPLORE_SLUG)
      : EXPLORE_SLUG.COURSE;

  const { productData, paginationData, exploreError, isLoading } =
    useExplorePreloadedData();

  const onPageChange = (page: number) => {
    addFilter({
      filterName: "page",
      value: String(page),
      name: "",
      withReplace: true,
    });
  };

  const getProductCardProps = (
    props: NewExploreProduct | NewExploreInstructor | NewExploreLanding
  ):
    | Omit<ICard, "variant">
    | NewExploreInstructor
    | Omit<ILandingCard, "variant"> => {
    if (slug === EXPLORE_SLUG.INSTRUCTOR) return props as NewExploreInstructor;

    const propsData = props as NewExploreProduct;
    if (slug === EXPLORE_SLUG.LANDING) {
      return {
        id: props.id,
        png_image: propsData.png_image || "",
        svg_image: propsData.svg_image || "",
        png_featured_image: propsData.png_featured_image || "",
        svg_featured_image: propsData.svg_featured_image || "",
        href: propsData.url,
        isFavorite: propsData.is_favorite,
        sku: propsData.sku,
        slug: propsData.slug,
        status: propsData.status,
        title: propsData.title_short || propsData.title,
        title_short: propsData.title_short,
        type: propsData.type,
        watchPercent: propsData.watchTime_Percent,
        duration: propsData.duration,
        festival: propsData.festival_new,
        instructor: propsData.instructors,
        instructorCount: propsData.instructors?.length,
        price: propsData.price,
        showDetailCard: true,
        showCourseTag: false,
        studentCount: (props as NewExploreLanding).total_sales,
        courseCount:
          slug === EXPLORE_SLUG.LANDING
            ? (props as NewExploreLanding).product_count ||
              (props as NewExploreLanding).subLandingProductCount
            : 0,

        showClock: true,
        showInstructor: true,
        hasCertificate: propsData.has_certificate || false,
      } as Omit<ILandingCard, "variant">;
    }

    return {
      id: props.id,
      png_image: propsData.png_image || "",
      svg_image: propsData.svg_image || "",
      png_featured_image: propsData.png_featured_image || "",
      svg_featured_image: propsData.svg_featured_image || "",
      isBuy: propsData.is_buy,
      isFavorite: propsData.is_favorite,
      sku: propsData.sku,
      slug: propsData.slug,
      status: propsData.status,
      title: propsData.title_short || propsData.title,
      title_short: propsData.title_short,
      type: propsData.type,
      watchPercent: propsData.watchTime_Percent,
      duration: propsData.duration,
      festival: propsData.festival_new,
      instructor: propsData.instructors,
      instructorCount: propsData.instructors?.length,
      price: propsData.price,
      showTag: true,
      hasCertificate: propsData.has_certificate || false,
      tag: propsData.has_certificate
        ? CARD_TAG.CERTIFICATE
        : propsData.type === COURSE_TYPE.VIDEO
          ? CARD_TAG.VIDEO
          : undefined,
      inCart: propsData.in_cart,
    };
  };

  return {
    paginationData,
    productData,
    exploreError,
    getProductCardProps,
    onPageChange,
    isLoading,
  };
};
